import cx from 'classnames';
import { useLayoutEffect } from 'react';
import styles from '../Table.module.scss';
import { flexRender, Table } from '@tanstack/react-table';
import { TableOptions } from '@/types/table';
import {
  ColumnOffsets,
  getColumnWidthStyle,
  getMergedGroupInfo,
  getMergedGroupWidth,
  getStickyColumnProps,
  PositionedColumnDef,
} from '../utils/columnLayout';
import { convertToCamelCase, getNextSortingState, getSortPriority } from '../utils/utils';
import RowExtensionHeader from './RowExtensionHeader';
import AdditionalHeaders from './AdditionalHeaders';
import SortingIndicator from './SortingIndicator';
import GroupingIndicator from './GroupingIndicator';
import { GroupResizeHandle, ColumnResizeHandle } from './ResizeHandles';
import { useHeaderTruncationTooltip } from '../hooks/useHeaderTruncationTooltip';

enum TableDataTestIds {
  TableHeaderRow = 'table-header-row',
}

type TableHeadProps<T> = {
  table: Table<T>;
  options?: TableOptions<T>;
  positionedColumnDefs: PositionedColumnDef<T>[];
  columnOffsets: ColumnOffsets;
};

const TableHead = <T,>({ table, options, positionedColumnDefs, columnOffsets }: TableHeadProps<T>) => {
  const showSortingPriorityBadge = !!options?.showSortingPriorityBadge;
  const isResizing = !!table.getState().columnSizingInfo.isResizingColumn;
  const { tooltipContext, resolvedTooltipPlace, handleHeaderMouseEnter, handleHeaderMouseLeave } =
    useHeaderTruncationTooltip({ tooltipPlace: options?.tooltipPlace });

  useLayoutEffect(() => {
    if (!isResizing) return;
    document.documentElement.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.documentElement.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (options?.hideTableHeaders) {
    return null;
  }

  const hasRowExtension = !!options?.rowExtension;

  return (
    <thead data-testid="table-header-row">
      {table.getHeaderGroups().map(headerGroup => (
        <tr key={headerGroup.id} className={styles.mainHeaderRow}>
          <RowExtensionHeader hasRowExtension={hasRowExtension} />
          {(() => {
            const visibleHeaders = headerGroup.headers?.filter(headerCell => headerCell.column.getIsVisible()) ?? [];

            return visibleHeaders.map((header, headerIndex) => {
              const columnDef = positionedColumnDefs[headerIndex];
              const mergedGroup = getMergedGroupInfo(positionedColumnDefs, headerIndex);

              if (mergedGroup && !mergedGroup.isFirst) return null;

              const {
                isStickyColumnLeft,
                isStickyColumnRight,
                isLastStickyColumn,
                isFirstStickyRightColumn,
                left,
                right,
              } = getStickyColumnProps(positionedColumnDefs, columnOffsets, headerIndex);

              const lastVisibleHeaderIndex = visibleHeaders.length - 1;
              const mergedGroupLastColumnIndex = mergedGroup ? headerIndex + mergedGroup.colSpan - 1 : -1;
              const isLastColumn = headerIndex === lastVisibleHeaderIndex;
              const isLastColumnInMergedGroup = !!mergedGroup && mergedGroupLastColumnIndex === lastVisibleHeaderIndex;

              const headerDisplayName = columnDef.headerDisplayName;
              const headerClassName = convertToCamelCase(headerDisplayName);

              const headerColumnClassName = cx({
                [styles.lastColumnHeader]: isLastColumn || isLastColumnInMergedGroup,
                [headerClassName]: true,
                [styles.sticky]: isStickyColumnLeft,
                [styles.stickyRight]: isStickyColumnRight,
                [styles.lastStickyColumn]: isLastStickyColumn,
                [styles.firstStickyRightColumn]: isFirstStickyRightColumn,
                [styles.mergedGroupHeader]: mergedGroup?.isFirst,
              });
              const mergedGroupWidth = getMergedGroupWidth(positionedColumnDefs, headerIndex);
              const width = mergedGroupWidth ?? columnDef.size;
              const widthStyle = getColumnWidthStyle(width);
              const isSorted = columnDef.sortable ? header.column.getIsSorted() : false;
              const sortPriority =
                columnDef.sortable && showSortingPriorityBadge
                  ? getSortPriority(table.getState().sorting, header.column.id)
                  : undefined;
              const sortDescFirst = header.column.getFirstSortDir() === 'desc';
              const handleSortClick = columnDef.sortable
                ? () =>
                    table.setSorting(currentSorting =>
                      getNextSortingState(currentSorting, header.column.id, sortDescFirst),
                    )
                : undefined;

              let colSpan = 1;
              if (mergedGroup) {
                colSpan = mergedGroup.colSpan;
              } else if (options?.verticalColumn && headerIndex === 0) {
                colSpan = options.verticalColumn.numberOfColspan;
              }

              const effectiveLeft = options?.verticalColumn && headerIndex === 0 ? 0 : left;

              return (
                <th
                  key={header.id}
                  data-testid={
                    TableDataTestIds.TableHeaderRow + '-' + headerDisplayName.toLowerCase()?.replace(/\s/g, '-')
                  }
                  style={{ width: widthStyle, minWidth: widthStyle, maxWidth: widthStyle, left: effectiveLeft, right }}
                  className={headerColumnClassName}
                  onClick={handleSortClick}
                  data-header-cell-separator={mergedGroup ? undefined : columnDef.headerCellSeparator}
                  data-merged-group={mergedGroup ? mergedGroup.groupLabel : undefined}
                  scope={mergedGroup ? 'colgroup' : 'col'}
                  colSpan={colSpan}
                >
                  <div
                    className={cx(styles.headerCellContent, {
                      [styles.sortable]: columnDef.sortable,
                      [styles.sorted]: isSorted,
                    })}
                    style={isResizing ? { pointerEvents: 'none' } : undefined}
                  >
                    <div className={styles.headerLabelContent}>
                      <GroupingIndicator header={header} />
                      <span
                        className={styles.headerCellText}
                        data-tooltip-id={tooltipContext?.id}
                        data-tooltip-hidden={true}
                        data-tooltip-place={resolvedTooltipPlace}
                        onMouseEnter={handleHeaderMouseEnter}
                        onMouseLeave={handleHeaderMouseLeave}
                      >
                        {mergedGroup
                          ? mergedGroup.groupLabel
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                    </div>
                    {columnDef.sortable && <SortingIndicator isSorted={isSorted} priority={sortPriority} />}
                  </div>
                  {header.column.getCanResize() && mergedGroup && (
                    <GroupResizeHandle
                      allHeaders={visibleHeaders}
                      startIndex={headerIndex}
                      colSpan={mergedGroup.colSpan}
                      table={table}
                    />
                  )}
                  {header.column.getCanResize() && !mergedGroup && <ColumnResizeHandle header={header} />}
                </th>
              );
            });
          })()}
        </tr>
      ))}
      <AdditionalHeaders
        table={table}
        options={options}
        positionedColumnDefs={positionedColumnDefs}
        columnOffsets={columnOffsets}
      />
    </thead>
  );
};

export default TableHead;
